import {
  Border,
  Button,
  CardHeader,
  ChildContainer,
  ColumnRight,
  Container,
  DownArrow,
  IconContainer,
  InitialText,
  Label,
  Row,
  TextContainer,
  Title,
  UpArrow,
} from './ExpandableCard.style';
import { Fragment, FunctionalComponent, h } from 'preact';
import { useEffect, useState } from 'preact/hooks';

import Toggle from '../Toggles/Toggle/Toggle';
import { UI_VARIANT } from '@usercentrics/cmp-browser-sdk';
import { memo } from 'preact/compat';
import { useDeviceState } from '../../hooks';
import { useSettingsContext } from '../../contexts/SettingsContext';
import { useUiContext } from '../../contexts/UiContext';

export type ToggleProps = {
  ariaLabel?: string;
  checked: boolean | null;
  defaultChecked?: boolean;
  id: string;
  isDisabled?: boolean;
  label?: string;
  onToggle: (checked: boolean) => void;
};

type Props = {
  title: string;
  description?: string;
  hideToggle: boolean;
  label?: JSX.Element;
  onToggleExpandable?: () => void;
  testid?: string;
  toggles?: ToggleProps[];
  type: string;
  useBoldTitle?: boolean;
  isExpandedOnInit?: boolean;
};

const ExpandableCard: FunctionalComponent<Props> = memo(
  ({
    children,
    description,
    label,
    onToggleExpandable,
    title,
    testid = 'uc-expandable-card',
    hideToggle = false,
    toggles,
    type,
    useBoldTitle = true,
    isExpandedOnInit = false,
  }) => {
    const [showExpandedContent, setShowExpandedContent] = useState(isExpandedOnInit);
    const [prevShowExpandedContent, setPrevShowExpandedContent] = useState<boolean | null>(null);
    const { isMobile } = useDeviceState();
    const { uiVariant, isAmpEnabled } = useUiContext();
    const { labels } = useSettingsContext();
    const isCcpa = uiVariant === UI_VARIANT.CCPA;
    const isTcf = uiVariant === UI_VARIANT.TCF;
    const hasToggle = toggles && toggles.length > 0;

    useEffect(() => {
      if (prevShowExpandedContent != null && onToggleExpandable) {
        onToggleExpandable();
      }
    }, [prevShowExpandedContent, showExpandedContent, onToggleExpandable]);

    if (!labels) {
      return null;
    }

    const onShowExpandableToggle = (): void => {
      setShowExpandedContent(!showExpandedContent);
      setPrevShowExpandedContent(!showExpandedContent);
    };

    return (
      <Container data-testid={testid} aria-label={title}>
        <CardHeader hasToggle={hasToggle} hideToggles={isAmpEnabled && !isTcf}>
          <TextContainer
            hasToggle={hasToggle}
            hideToggles={isAmpEnabled && !isTcf}
            onClick={onShowExpandableToggle}
            aria-expanded={showExpandedContent ? 'true' : 'false'}
          >
            <Title useBoldTitle={useBoldTitle}>{title}</Title>
            {description && <InitialText>{description}</InitialText>}
            {label && <Label>{label}</Label>}
          </TextContainer>
          <ColumnRight hideToggles={isAmpEnabled && !isTcf}>
            {toggles && (
              <Row reverseDirection={isMobile}>
                {toggles.map((toggleProps) => (
                  <Toggle
                    ariaLabel={toggleProps.ariaLabel}
                    checked={toggleProps.checked === null ? toggleProps.defaultChecked : toggleProps.checked}
                    hideToggle={hideToggle || isCcpa || (isAmpEnabled && !isTcf)}
                    id={`toggle-${toggleProps.id}`}
                    isDisabled={toggleProps.isDisabled}
                    label={toggleProps.label}
                    onToggle={toggleProps.onToggle}
                  />
                ))}
              </Row>
            )}
            <Button
              data-testid={`uc-expandable-button-${title.toLowerCase()}`}
              hasToggle={hasToggle}
              hideToggles={isAmpEnabled && !isTcf}
              onClick={onShowExpandableToggle}
              role="button"
              aria-label={
                showExpandedContent
                  ? `${labels.ariaLabels.collapse} ${title} ${type} ${labels.ariaLabels.usercentricsCard}`
                  : `${labels.ariaLabels.expand} ${title} ${type} ${labels.ariaLabels.usercentricsCard}`
              }
              aria-expanded={showExpandedContent ? 'true' : 'false'}
            >
              <IconContainer>{showExpandedContent ? <UpArrow /> : <DownArrow />}</IconContainer>
            </Button>
          </ColumnRight>
        </CardHeader>

        {showExpandedContent && (
          <Fragment>
            <Border />
            <ChildContainer data-testid="uc-expandable-content">{children}</ChildContainer>
          </Fragment>
        )}
      </Container>
    );
  },
);

export default ExpandableCard;
